import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import { Client, clientApi } from '../services/clientApi';
import { DietProgram, dietProgramApi } from '../services/dietProgramApi';
import { Dietitian, dietitianApi } from '../services/dietitianApi';

interface UseClientDataOptions {
  fetchAppointments?: boolean;
  fetchDietPrograms?: boolean;
  fetchDietitian?: boolean;
}

export const useClientData = (options: UseClientDataOptions = {}) => {
  const {
    fetchAppointments = false,
    fetchDietPrograms = false,
    fetchDietitian = false,
  } = options;

  // Options'ı ref'te sakla ki her render'da yeni obje oluşturulmasın
  const optionsRef = useRef({
    fetchAppointments,
    fetchDietPrograms,
    fetchDietitian,
  });
  optionsRef.current = { fetchAppointments, fetchDietPrograms, fetchDietitian };

  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  const {
    clientId: contextClientId,
    client: contextClient,
    userEmail: contextUserEmail,
    loadClient,
    setClient: setAuthClient,
  } = useAuth();
  const [userEmail, setUserEmail] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [client, setClient] = useState<Client | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [dietPrograms, setDietPrograms] = useState<DietProgram[]>([]);
  const [dietitian, setDietitian] = useState<Dietitian | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Role kontrolü: Çıkış yapmışsa sadece yönlendir; yanlış roldeyse hata göster
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const storedEmail = localStorage.getItem('userEmail');

    if (!storedEmail || !userRole) {
      navigate('/client/login');
      return;
    }
    if (userRole !== 'client') {
      showError(
        'Bu sayfaya erişim yetkiniz yok. Lütfen danışan panelinden giriş yapın.',
        'Erişim Engellendi'
      );
      navigate('/client/login');
      return;
    }

    setUserEmail(storedEmail);
  }, [navigate, showError]);

  // Context'ten client verisini senkron göster; yoksa API'den yükle (localStorage atlanır, güncel veri için)
  useEffect(() => {
    if (contextClientId && contextClient) {
      setClientId(contextClientId);
      setClient(contextClient);
    } else if (contextUserEmail && !contextClient) {
      loadClient(contextUserEmail, { forceRefresh: true }).catch((error) => {
        console.error('Client yükleme hatası:', error);
      });
    }
  }, [contextClientId, contextClient, contextUserEmail, loadClient]);

  // Verileri çek: her seferinde API'den güncel client al (diyetisyen kabul sonrası görünsün)
  useEffect(() => {
    if (userEmail && userEmail !== '') {
      fetchClientData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // Pencere odağa gelince client verisini yenile (diyetisyen kabul etmiş olabilir)
  useEffect(() => {
    if (!userEmail) return;
    const onFocus = () => {
      loadClient(userEmail, { forceRefresh: true }).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userEmail, loadClient]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { fetchAppointments, fetchDietPrograms, fetchDietitian } =
        optionsRef.current;

      // Her zaman API'den güncel client al; diyetisyen kabul sonrası dietitianId gelsin
      const clientData = await clientApi.getClientByEmail(userEmail);
      if (!clientData || !clientData.id) {
        throw new Error('Client verisi yüklenemedi');
      }
      setAuthClient(clientData);
      setClientId(clientData.id);
      setClient(clientData);

      // Sadece gerektiğinde ek verileri çek
      const promises: Promise<any>[] = [];

      if (fetchDietitian && (clientData.dietitianEmail || clientData.dietitianId)) {
        const fetchDietitianPromise = clientData.dietitianEmail
          ? dietitianApi.getDietitianByEmail(clientData.dietitianEmail)
          : dietitianApi.getDietitianById(clientData.dietitianId!);
        console.log('👨‍⚕️ Diyetisyen yükleniyor:', clientData.dietitianEmail || clientData.dietitianId);
        promises.push(
          fetchDietitianPromise
            .then((dietitianData) => {
              console.log('✅ Diyetisyen verisi geldi:', dietitianData);
              setDietitian(dietitianData);
            })
            .catch((err: any) => {
              console.error('❌ Diyetisyen verisi alınamadı:', err);
              console.log(
                '💡 Bu diyetisyen henüz profil oluşturmamış olabilir.'
              );
            })
        );
      }

      if (fetchAppointments) {
        promises.push(
          appointmentApi
            .getAppointmentsByClient(clientData.id)
            .then((appointmentsData) => {
              setAppointments(appointmentsData);
            })
        );
      }

      if (fetchDietPrograms) {
        promises.push(
          dietProgramApi
            .getDietProgramsByClient(clientData.id)
            .then((dietProgramsData) => {
              setDietPrograms(dietProgramsData);
            })
        );
      }

      await Promise.all(promises);
    } catch (err) {
      setError('Veriler yüklenirken hata oluştu');
      console.error('Error fetching client data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchClientData();
  };

  const getCurrentDietProgram = () => {
    return (
      dietPrograms.find((program) => program.status === 'Aktif') ||
      dietPrograms[0]
    );
  };

  const getNextAppointment = () => {
    const now = new Date();
    return appointments
      .filter(
        (apt) =>
          new Date(apt.appointmentDate) > now && apt.status === 'Planlandı'
      )
      .sort(
        (a, b) =>
          new Date(a.appointmentDate).getTime() -
          new Date(b.appointmentDate).getTime()
      )[0];
  };

  const getLastAppointment = () => {
    const now = new Date();
    return appointments
      .filter((apt) => new Date(apt.appointmentDate) <= now)
      .sort(
        (a, b) =>
          new Date(b.appointmentDate).getTime() -
          new Date(a.appointmentDate).getTime()
      )[0];
  };

  return {
    userEmail,
    clientId,
    client,
    appointments,
    dietPrograms,
    dietitian,
    loading,
    error,
    setClient,
    setAppointments,
    setDietPrograms,
    refreshData,
    getCurrentDietProgram,
    getNextAppointment,
    getLastAppointment,
    showSuccess,
    showError,
    showInfo,
  };
};
